import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useStore } from '../store';
import { ROLES, isEventsAdmin, EVENTS_ADMIN_ALLOWED_PATHS } from '../config/role';
import { UserType } from '../@types/userRoles';
import useKeepAlive from '../hook/useKeepAlive';

const SignIn = lazy(() => import('../pages/SignIn').then((m) => ({ default: m.SignIn })));
const Layout = lazy(() => import('../pages/Layout').then((m) => ({ default: m.Layout })));
const Home = lazy(() => import('../pages/Home').then((m) => ({ default: m.Home })));
const Dashboard = lazy(() => import('../pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Clients = lazy(() => import('../pages/Invites/index').then((m) => ({ default: m.Clients })));
const Contacts = lazy(() => import('../pages/Contacts').then((m) => ({ default: m.Contacts })));
const Coupons = lazy(() => import('../pages/Coupons').then((m) => ({ default: m.Coupons })));
const Categories = lazy(() => import('../pages/Categories').then((m) => ({ default: m.Categories })));
const Approves = lazy(() => import('../pages/Approves').then((m) => ({ default: m.Approves })));
const AntiFakeBot = lazy(() => import('../pages/AntiFakeBot').then((m) => ({ default: m.AntiFakeBot })));
const RejectionTemplates = lazy(() =>
  import('../pages/RejectionTemplates').then((m) => ({ default: m.RejectionTemplates })),
);
const UserJourney = lazy(() => import('../pages/UserJourney').then((m) => ({ default: m.UserJourney })));
const Revisao = lazy(() => import('../pages/Revisao').then((m) => ({ default: m.Revisao })));
const Employees = lazy(() => import('../pages/Employees').then((m) => ({ default: m.Employees })));
const Payments = lazy(() => import('../pages/Payments').then((m) => ({ default: m.Payments })));
const PaymentIssues = lazy(() => import('../pages/Payments/PaymentIssues').then((m) => ({ default: m.PaymentIssues })));
const Plans = lazy(() => import('../pages/Plans').then((m) => ({ default: m.Plans })));
const Users = lazy(() => import('../pages/Users').then((m) => ({ default: m.Users })));
const Leads = lazy(() => import('../pages/Leads').then((m) => ({ default: m.Leads })));
const Posts = lazy(() => import('../pages/Posts').then((m) => ({ default: m.Posts })));
const Public = lazy(() => import('../pages/Public').then((m) => ({ default: m.Public })));
const AdmPosts = lazy(() => import('../pages/AdmPost').then((m) => ({ default: m.AdmPosts })));
const Faq = lazy(() => import('../pages/Faq').then((m) => ({ default: m.Faq })));
const ConfigPgto = lazy(() => import('../pages/PaymentConfig').then((m) => ({ default: m.ConfigPgto })));
const PBProfilesPage = lazy(() => import('../pages/PbProfiles'));
const RobotMatchPage = lazy(() => import('../pages/RobotMatch'));
const RobotInterestsPage = lazy(() => import('../pages/RobotInterests'));
const RobotQualificationsPage = lazy(() => import('../pages/RobotQualifications'));
const AdmPostsFeed = lazy(() => import('../pages/AdmPostsFeed').then((m) => ({ default: m.AdmPostsFeed })));
const PostCards = lazy(() => import('../pages/PostCards').then((m) => ({ default: m.PostCards })));
const AmbassadorProfilesPage = lazy(() =>
  import('../pages/AmbassadorProfiles/AmbassadorProfiles').then((m) => ({ default: m.AmbassadorProfilesPage })),
);
const AbsentAlertPage = lazy(() => import('../pages/AlertAbsent'));
const ClassDetails = lazy(() => import('../pages/Class/ClassDetails'));
const ListClass = lazy(() => import('../pages/Class/ListClass'));
const Events = lazy(() => import('../pages/Events').then((m) => ({ default: m.Events })));
const CredentialsPage = lazy(() => import('../pages/Credentials/CredentialsPage'));
const EditUserInterests = lazy(() => import('../pages/Interests/EditUserInterests'));
const HotCoinsPage = lazy(() => import('../pages/HotCoins'));
const AccountBlock = lazy(() => import('../pages/AccountBlock').then((m) => ({ default: m.AccountBlock })));
const FeatureFlagsPage = lazy(() => import('../pages/FeatureFlags').then((m) => ({ default: m.FeatureFlagsPage })));
const PromoInviteRewardsPage = lazy(() =>
  import('../pages/PromoInviteRewards').then((m) => ({ default: m.PromoInviteRewardsPage })),
);
const IntegrityPage = lazy(() => import('../pages/Integrity').then((m) => ({ default: m.IntegrityPage })));

function LoadingFallback() {
  return (
    <div className='flex min-h-screen items-center justify-center'>
      <div className='flex flex-col items-center gap-4'>
        <div className='size-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent' />
        <p className='text-sm text-gray-600'>Carregando...</p>
      </div>
    </div>
  );
}

export function Router() {
  const user = useStore((store) => store.user);

  function RequireAuth({ children }: { children: JSX.Element }) {
    const location = useLocation();

    if (!user.id) {
      return (
        <Navigate
          to='/'
          state={{ from: location }}
          replace
        />
      );
    }
    return children;
  }

  const RequireRole = ({ children, minRole }: { children: JSX.Element; minRole: number }) => {
    const loc = useLocation();
    if (isEventsAdmin(user.user_type)) {
      if (EVENTS_ADMIN_ALLOWED_PATHS.includes(loc.pathname)) {
        return children;
      }
      return (
        <Navigate
          to='/events'
          state={{ from: loc }}
          replace
        />
      );
    }

    const level = ROLES[user.user_type as keyof UserType];
    if (level <= minRole) {
      return children;
    }

    return (
      <Navigate
        to='/home'
        state={{ from: loc }}
        replace
      />
    );
  };

  useKeepAlive();

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path='/'
            element={<SignIn />}
          />
          <Route
            path='/home'
            element={
              <RequireAuth>
                <Layout>
                  <Home />
                </Layout>
              </RequireAuth>
            }
          />
          <Route
            path='/invitations'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Clients />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/employees'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <Employees user_type={user.user_type as keyof UserType} />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/users'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <Users />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/leads'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <Leads />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/interests/edit'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <EditUserInterests />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/payments'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <Payments />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/payment-config'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <ConfigPgto />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/payments/issues'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <PaymentIssues />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/payments/coupons'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Coupons />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/posts'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <Posts />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/credentials'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <CredentialsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/adm-posts'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <AdmPosts />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/adm-postsFeed'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <AdmPostsFeed />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/events'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <Events />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/post-cards'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <PostCards />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/alert-absent'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <AbsentAlertPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/public-figure'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Public />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/pb-profiles'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <PBProfilesPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/ambassador-profiles'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <AmbassadorProfilesPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/class'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <ListClass />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/hotcoins'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <HotCoinsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/class/:id'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <ClassDetails />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/payments/plans'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Plans />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/dashboard'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/categories'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <Categories />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/approves'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <Approves />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/antiFakeBot'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <AntiFakeBot />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/antiFakeBot/rejection-templates'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <RejectionTemplates />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/user-journey'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <UserJourney />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/revisao'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <Revisao />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/account-block'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <AccountBlock />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/faq'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <Faq />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/robot-match'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <RobotMatchPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/robot-interests'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <RobotInterestsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/robot-qualifications'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <RobotQualificationsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/contacts'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.MANAGER}>
                  <Layout>
                    <Contacts />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/feature-flags'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <FeatureFlagsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/promotions/invite-rewards'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.ADMIN}>
                  <Layout>
                    <PromoInviteRewardsPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
          <Route
            path='/integrity'
            element={
              <RequireAuth>
                <RequireRole minRole={ROLES.SUPERADMIN}>
                  <Layout>
                    <IntegrityPage />
                  </Layout>
                </RequireRole>
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
